import React, { FunctionComponent, memo, useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Dropdown, List, Button } from "@shared/components";
import { ProfileMenuOption, ProfileMenuOptionLabel } from "@app/models/Header";
import { getProfileMenuOptions } from "@app/utils/getProfileMenuOptions";
import { ProfileMenuPermissions } from "@app/hooks";
import { ReactComponent as ProfileIcon } from "@assets/svg/profile_icon.svg";
import { ReactComponent as Arrow } from "@assets/svg/arrow_dropdown.svg";

interface Props {
  name: string;
  /** Profile menu permissions determining which options to show */
  menuPermissions: ProfileMenuPermissions;
  iconClassName: string;
  onMenuOptionClick(option: ProfileMenuOption): void;
  onSignOutButtonClick(): void;
}

const useStyles = makeStyles((theme) => ({
  name: {
    color: theme.palette.primary.main,
    padding: theme.spacing(6, 5, 3),
    fontSize: 24,
    lineHeight: 1,
  },
  button: {
    padding: theme.spacing(3, 5, 6),
  },
  listEndAdornment: {
    transform: "rotate(-90deg)",
    width: 10,
  },
}));

/**
 * Profile dropdown menu component.
 *
 * Displays user name and menu options based on user's permissions.
 * Menu visibility is determined by the menuPermissions prop which considers:
 * - User's role (Primary Contact, Secondary Contact, etc.)
 * - Fund access type (Internal vs External)
 * - IFC status (whether user has fund assignments)
 * - Admin status (canImpersonate)
 */
const ProfileDropdown: FunctionComponent<Props> = ({
  name,
  menuPermissions,
  iconClassName,
  onMenuOptionClick,
  onSignOutButtonClick,
}) => {
  const classes = useStyles();

  const listEndAdornment = useMemo(() => <Arrow className={classes.listEndAdornment} />, [classes.listEndAdornment]);

  // Build menu options based on permissions (role + fund access + admin status)
  const dropdownOptions = useMemo(
    () =>
      getProfileMenuOptions(menuPermissions).map((option) => ({
        ...option,
        endAdornment: listEndAdornment,
      })),
    [menuPermissions, listEndAdornment]
  );

  return (
    <Dropdown
      withArrow
      stretchToContentWidth
      width={230}
      placement="bottom-end"
      renderToggle={({ ref, toggleDropdown }) => (
        <Tooltip title={<Typography>My Account</Typography>} placement="top">
          <ProfileIcon ref={ref} className={iconClassName} onClick={toggleDropdown} />
        </Tooltip>
      )}
    >
      {({ closeDropdown }) => (
        <>
          <Typography className={classes.name} variant="h2">
            {name.split(" ").map((word) => (
              <p key={word}>{word}</p>
            ))}
          </Typography>
          <List
            variant="singleselect"
            options={dropdownOptions}
            onOptionClick={(option) => {
              onMenuOptionClick(option);
              closeDropdown();
            }}
          />
          <Box className={classes.button}>
            <Button fullWidth variant="outlined" shape="rounded" onClick={onSignOutButtonClick}>
              {ProfileMenuOptionLabel.SignOut}
            </Button>
          </Box>
        </>
      )}
    </Dropdown>
  );
};

export default memo<Props>(ProfileDropdown);
